import type { ReactElement } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import GoogleMapReact, { fitBounds } from 'google-map-react'
import Marker from 'static/components/StoreLocator/Marker'
import { GOOGLE_API_KEY } from 'utils/constants'
import { getSupercluster, onClusterClick } from 'utils/mapUtils'
import ClusterMarkerBase from 'components/common/GoogleMapsClusterMarker'
import styled from 'styled-components'
import type { LocaleObject, LanguageCode } from 'utils/language'
import type { BBox } from 'geojson'

const ClusterMarker = styled(ClusterMarkerBase)`
	cursor: pointer;
`

let map: any

interface LatLng {
	lat: number
	lng: number
}

interface Bounds {
	nw: LatLng
	se: LatLng
	sw: LatLng
	ne: LatLng
}
type Zoom = number

interface StoreItem {
	address: {
		formatted: string
		city: string
		latLng: LatLng
		countryCode: string
		properties: Record<string, string>
	} & Record<string, any>
}

interface MapProps {
	locale: LocaleObject
	lang: LanguageCode
	stores: StoreItem[]
	currentLocation: LatLng
	onSelectedStore: (store: any) => void
	hasStores: boolean
	defaultLocation: LatLng
}

const Map = ({ locale, lang, stores, currentLocation, onSelectedStore, hasStores, defaultLocation }: MapProps) => {
	const [center, setCenter] = useState<LatLng>(currentLocation)
	const [zoom, setZoom] = useState<Zoom>()
	const [markers, setMarkers] = useState<ReactElement[]>([])

	const superCluster = useMemo(() => getSupercluster(stores), [stores])

	useEffect(() => {
		if (currentLocation) {
			setCenter(currentLocation)
		}
	}, [currentLocation])

	const setCenterAndZoomOnMap = () => {
		if (stores.length > 1) {
			// we need 2 lat,lng pairs for the fitBounds API below. the pair is: NE and SW OR NW and SE
			const maxLat = Math.max(...stores.map(({ address }) => address.latLng.lat))
			const maxLng = Math.max(...stores.map(({ address }) => address.latLng.lng))

			const minLat = Math.min(...stores.map(({ address }) => address.latLng.lat))
			const minLng = Math.min(...stores.map(({ address }) => address.latLng.lng))

			const bounds = {
				ne: {
					lat: maxLat,
					lng: maxLng,
				},
				sw: {
					lat: minLat,
					lng: minLng,
				},
			}

			const mapElement = document.querySelector('#map-container') as HTMLElement
			const width = mapElement?.offsetWidth
			const height = mapElement?.offsetHeight
			const { center: centerBounds, zoom } = fitBounds(bounds, { width, height })
			setCenter(centerBounds)
			setZoom(zoom)
		}
	}

	const apiIsLoaded = (currentMap: any) => {
		map = currentMap
		setCenterAndZoomOnMap()
	}

	const handleClusterClick = (cluster: any) => {
		onClusterClick(cluster, superCluster, (zoomLevel: number, latLng: LatLng) => {
			setZoom(zoomLevel)
			setCenter(latLng)
			map?.panTo(latLng)
		})
	}

	const getMarkers = (mapBounds: BBox, mapZoom: Zoom) => {
		const clusters = superCluster.getClusters(mapBounds, mapZoom)

		return clusters.map((cluster) => {
			const [longitude, latitude] = cluster.geometry.coordinates
			const { cluster: isCluster, point_count: pointCount, store } = cluster.properties

			if (isCluster) {
				return (
					<ClusterMarker
						key={cluster.id}
						// @ts-ignore lat,lng must be provided as props according to google-map-react docs
						lat={latitude}
						lng={longitude}
						pointCount={pointCount}
						onClusterClick={() => handleClusterClick(cluster)}
					/>
				)
			}

			return (
				<Marker
					key={store.id}
					// @ts-ignore lat,lng must be provided as props according to google-map-react docs
					lat={store.address.latLng.lat}
					lng={store.address.latLng.lng}
					onSelectStore={() => onSelectedStore(store)}
					formatted={store.address.formatted}
					rank={store.storeRank}
					hasStores={hasStores}
					store={store}
					lang={lang}
				/>
			)
		})
	}

	const onMapChange = ({ zoom, bounds }: { zoom: Zoom; bounds: Bounds }) => {
		const mapBounds: BBox = [bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat]
		setZoom(zoom)
		setMarkers(getMarkers(mapBounds, zoom))
	}

	return (
		<GoogleMapReact
			yesIWantToUseGoogleMapApiInternals
			onGoogleApiLoaded={({ map }) => apiIsLoaded(map)}
			options={{
				clickableIcons: false,
				fullscreenControl: false,
				gestureHandling: 'greedy',
			}}
			bootstrapURLKeys={{ key: locale.key?.trim() || GOOGLE_API_KEY, libraries: ['places'] }}
			defaultCenter={defaultLocation}
			defaultZoom={6}
			center={center}
			zoom={zoom}
			onChange={onMapChange}
		>
			{markers}
		</GoogleMapReact>
	)
}

export default Map
